import type { TtsVoice } from '@/domains/types'

/** Voice names matching these are treated as high-quality (natural/neural
 * TTS engines): Chrome's "Google ... Neural2", Edge's "Microsoft ... Online
 * (Natural)", macOS/iOS's "Siri" voices, etc. */
const HIGH_QUALITY_PATTERN = /natural|neural|online|premium|enhanced|siri/i

/** Voice names matching these are the low-quality embedded engines some
 * Linux/Android builds ship as their only offline voices. */
const LOW_QUALITY_PATTERN = /espeak|pico|mbrola/i

/** Classifies a voice's quality from its display name. Anything that is
 * neither clearly high nor clearly low quality — including plain "Google
 * français"-style voices — is `'standard'`. */
export function classifyVoiceQuality(voiceName: string): TtsVoice['quality'] {
  if (HIGH_QUALITY_PATTERN.test(voiceName)) return 'high'
  if (LOW_QUALITY_PATTERN.test(voiceName)) return 'low'
  return 'standard'
}

const QUALITY_RANK: Record<TtsVoice['quality'], number> = { high: 0, standard: 1, low: 2 }

/** Default region preference per language when the visitor's own
 * `navigator.languages` doesn't name a region for that language. */
const DEFAULT_REGION_ORDER: Record<string, string[]> = {
  fr: ['FR', 'BE', 'CH', 'CA'],
  en: ['GB', 'US', 'AU', 'IE'],
  es: ['ES', 'MX', 'US', 'AR'],
}

function normalizeLang(lang: string): string {
  return lang.replace(/_/g, '-').toLowerCase()
}

function langPrefix(lang: string): string {
  return normalizeLang(lang).split('-')[0] ?? ''
}

function region(lang: string): string | null {
  const parts = normalizeLang(lang).split('-')
  return parts[1] ? parts[1].toUpperCase() : null
}

/**
 * Ranks the voices available for `lang` (matched by normalized language
 * prefix — `fr_CA` and `fr-FR` both match `fr`) best-first: quality first
 * (high > standard > low), then region — the visitor's own
 * `navigator.languages` region for that language if any voice has it,
 * otherwise the language's default region order, unlisted regions last —
 * then the browser's own "default voice" flag as a final tie-breaker.
 */
export function rankVoices(
  voices: readonly TtsVoice[],
  lang: string,
  navigatorLanguages: readonly string[] = [],
): TtsVoice[] {
  const prefix = langPrefix(lang)
  const matching = voices.filter((voice) => langPrefix(voice.lang) === prefix)

  const navigatorRegion = navigatorLanguages
    .map((navLang) => (langPrefix(navLang) === prefix ? region(navLang) : null))
    .find((value): value is string => value !== null)

  const fallbackRegions = DEFAULT_REGION_ORDER[prefix] ?? []

  function regionRank(voice: TtsVoice): number {
    const voiceRegion = region(voice.lang)
    if (voiceRegion && navigatorRegion && voiceRegion === navigatorRegion) return -1
    const index = voiceRegion ? fallbackRegions.indexOf(voiceRegion) : -1
    return index === -1 ? fallbackRegions.length : index
  }

  return [...matching].sort((left, right) => {
    const qualityDiff = QUALITY_RANK[left.quality] - QUALITY_RANK[right.quality]
    if (qualityDiff !== 0) return qualityDiff

    const regionDiff = regionRank(left) - regionRank(right)
    if (regionDiff !== 0) return regionDiff

    if (left.isDefault !== right.isDefault) return left.isDefault ? -1 : 1
    return 0
  })
}

/**
 * Picks the best voice for `lang`: the caller's still-available preferred
 * voice id if given, otherwise the best-ranked voice for that language (or
 * `null` when none is available at all — the caller falls back to speaking
 * with only `lang` set on the utterance).
 */
export function selectVoice({
  voices,
  lang,
  preferredVoiceId,
  navigatorLanguages = [],
}: {
  voices: readonly TtsVoice[]
  lang: string
  preferredVoiceId?: string | null
  navigatorLanguages?: readonly string[]
}): TtsVoice | null {
  if (preferredVoiceId) {
    const preferred = voices.find((voice) => voice.id === preferredVoiceId)
    if (preferred && langPrefix(preferred.lang) === langPrefix(lang)) {
      return preferred
    }
  }

  const ranked = rankVoices(voices, lang, navigatorLanguages)
  return ranked[0] ?? null
}
